import { useEffect, useState } from "react";
import { Loader2, MailWarning, Send, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

/** Weekly digest to each manager listing only their team's repeat late
 *  starters (late on N+ days over the lookback window). Shown to staff with
 *  inout.manage. */
export const LateDigestCard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/inout/late-digest")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const patch = async (upd) => {
    setSaving(true);
    try {
      const r = await api.put("/inout/late-digest", upd);
      setData((d) => ({ ...d, config: r.data }));
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
      load();
    } finally {
      setSaving(false);
    }
  };

  const send = async (test) => {
    if (!test && !window.confirm("Send each manager their repeat-late-start digest now? Each manager only sees their own team.")) return;
    setSending(test ? "test" : "real");
    try {
      const r = await api.post("/inout/late-digest/send-now", { test });
      const st = r.data?.status;
      if (test) {
        if (st === "sent") toast.success(`Preview sent to ${r.data.to}`);
        else if (st === "paused") toast.warning("Email is paused platform-wide — unpause on the Dashboard banner.");
        else toast.error("Couldn't send the preview.");
      } else {
        toast.success(`Sent to ${r.data?.sent || 0} manager(s)`);
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send");
    } finally {
      setSending("");
    }
  };

  const cfg = data?.config || {};
  const preview = data?.preview || {};
  const canManage = data?.can_manage;
  const managers = preview.managers || [];
  const unassigned = preview.unassigned || [];

  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm" data-testid="late-digest-card">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <MailWarning className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">Repeat late-start digest</span>
        <span className="text-[11px] text-muted-foreground">
          Weekly email to each manager — only their team's repeat late starters
        </span>
        {canManage && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground">{cfg.enabled ? "On" : "Off"}</span>
            <Switch checked={!!cfg.enabled} disabled={saving}
                    onCheckedChange={(v) => patch({ enabled: v })} data-testid="late-digest-enabled" />
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <>
          {canManage && (
            <div className="mb-3 flex flex-wrap items-end gap-4 rounded-lg border border-border bg-muted/30 px-3 py-2">
              <label className="text-xs text-muted-foreground">
                Repeat = late on
                <Input type="number" min={1} max={7} defaultValue={cfg.min_days}
                       onBlur={(e) => { const v = Math.max(1, Math.min(7, +e.target.value || 2)); if (v !== cfg.min_days) patch({ min_days: v }); }}
                       className="ml-2 inline-block h-7 w-14 text-center text-xs" data-testid="late-digest-min-days" />
                <span className="ml-1">+ days</span>
              </label>
              <label className="text-xs text-muted-foreground">
                Look back
                <Input type="number" min={2} max={30} defaultValue={cfg.lookback_days}
                       onBlur={(e) => { const v = Math.max(2, Math.min(30, +e.target.value || 7)); if (v !== cfg.lookback_days) patch({ lookback_days: v }); }}
                       className="ml-2 inline-block h-7 w-16 text-center text-xs" data-testid="late-digest-lookback" />
                <span className="ml-1">days</span>
              </label>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => send(true)} disabled={!!sending}
                        className="h-7 gap-1.5 text-xs" data-testid="late-digest-send-preview">
                  {sending === "test" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Send preview to me
                </Button>
                <Button size="sm" onClick={() => send(false)} disabled={!!sending || managers.length === 0}
                        title={managers.length === 0 ? "No managers have repeat late starters assigned to them in this window" : "Email each manager their team's repeat late starters"}
                        className="h-7 gap-1.5 text-xs" data-testid="late-digest-send-managers">
                  {sending === "real" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Users className="h-3.5 w-3.5" />}
                  Send to managers now
                </Button>
              </div>
            </div>
          )}

          <div className="text-xs text-muted-foreground" data-testid="late-digest-summary">
            {preview.total_people ? (
              <>
                <b className="text-foreground">{preview.total_people}</b> repeat late starter{preview.total_people === 1 ? "" : "s"} between{" "}
                <b className="text-foreground">{preview.since}</b> and <b className="text-foreground">{preview.until}</b>{" "}
                (late on {preview.min_days}+ days).
              </>
            ) : (
              <>No repeat late starters between {preview.since} and {preview.until}.</>
            )}
          </div>

          {(managers.length > 0 || unassigned.length > 0) && (
            <ul className="mt-2 space-y-1.5" data-testid="late-digest-manager-list">
              {managers.map((m) => (
                <li key={m.manager_id} className="rounded-lg border border-border px-3 py-2" data-testid={`late-digest-manager-${m.manager_id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{m.manager_name}</span>
                    <span className="text-[11px] text-muted-foreground">{m.manager_email}</span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {m.people.map((p) => `${p.name} (${p.late_days}d)`).join(" · ")}
                  </div>
                </li>
              ))}
              {unassigned.length > 0 && (
                <li className="rounded-lg border border-dashed border-border px-3 py-2" data-testid="late-digest-unassigned">
                  <div className="text-sm font-medium text-foreground">No manager assigned</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {unassigned.map((p) => `${p.name} (${p.late_days}d)`).join(" · ")}
                  </div>
                </li>
              )}
            </ul>
          )}

          {data?.last_send && (
            <div className="mt-3 text-[11px] text-muted-foreground" data-testid="late-digest-last-send">
              Last sent {new Date(data.last_send.sent_at).toLocaleString()} — {data.last_send.managers_notified || 0} manager(s).
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LateDigestCard;
